import { useState, useEffect } from 'react';
import { getPagesRegistry, updateUserRole, updatePageAccess } from '../../api/adminService';
import '../../styles/auth.scss';

const ROLES = ['viewer', 'analyst', 'admin'];

const ROLE_META = {
  viewer:  { label: 'Viewer',  color: '#3b82f6', bg: 'rgba(59,130,246,0.15)',  desc: 'Read-only access to permitted pages' },
  analyst: { label: 'Analyst', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', desc: 'Advanced access including strategy & astrology' },
  admin:   { label: 'Admin',   color: '#ef4444', bg: 'rgba(239,68,68,0.15)',   desc: 'Full access to all pages and admin panel' },
};

const UserAccessModal = ({ user, onClose, onSave }) => {
  const [pages, setPages]       = useState([]);
  const [access, setAccess]     = useState({});
  const [role, setRole]         = useState(user.role || 'viewer');
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState('');

  // Build default access from registry for a given role
  const buildDefaultAccess = (pgs, selectedRole) =>
    Object.fromEntries(pgs.map((p) => [p.key, p.defaultRoles?.includes(selectedRole) ?? false]));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await getPagesRegistry();
        const pgs = data.pages.filter((p) => p.category !== 'Admin');
        setPages(pgs);

        // Seed current user access (fall back to role defaults for missing keys)
        const defaults = buildDefaultAccess(pgs, user.role || 'viewer');
        const userAccess = { ...defaults };
        if (user.pageAccess) {
          Object.entries(user.pageAccess).forEach(([key, value]) => {
            userAccess[key] = value;
          });
        }
        setAccess(userAccess);
      } catch (err) {
        console.error('Failed to load pages:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  // When role dropdown changes → apply default access for that role
  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setAccess(buildDefaultAccess(pages, newRole));
    setSaveMsg('');
  };

  const togglePage = (key) => setAccess((prev) => ({ ...prev, [key]: !prev[key] }));

  const grantAll = () => setAccess(Object.fromEntries(pages.map((p) => [p.key, true])));
  const revokeAll = () => setAccess(Object.fromEntries(pages.map((p) => [p.key, false])));

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      // 1. Update role if changed
      if (role !== user.role) {
        await updateUserRole(user._id, role);
      }
      // 2. Apply the exact page access toggles (overrides role defaults)
      await updatePageAccess(user._id, access);
      onSave(user._id, access, role);
      onClose();
    } catch (err) {
      setSaveMsg(err?.response?.data?.message || 'Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  // Group pages by category (exclude Admin)
  const pagesByCategory = {};
  pages.forEach((page) => {
    if (!pagesByCategory[page.category]) pagesByCategory[page.category] = [];
    pagesByCategory[page.category].push(page);
  });

  const grantedCount = Object.values(access).filter(Boolean).length;
  const roleMeta = ROLE_META[role] || ROLE_META.viewer;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: '95vw' }}>

        {/* ── Header ── */}
        <div className="modal-header">
          <div>
            <h2>🔑 User Access Control</h2>
            <p style={{ marginTop: 4, fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)' }}>
              <strong style={{ color: '#a78bfa' }}>{user.name}</strong>
              &nbsp;·&nbsp;{user.email}
            </p>
          </div>
          <button onClick={onClose} className="btn-close">×</button>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <div className="spinner" />
            <p>Loading…</p>
          </div>
        ) : (
          <>
            {/* ── Role selector ── */}
            <div style={S.roleSection}>
              <div style={S.roleSectionLabel}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  User Role
                </span>
                <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>
                  Changing role resets page access to defaults
                </span>
              </div>

              <div style={S.roleCards}>
                {ROLES.map((r) => {
                  const m = ROLE_META[r];
                  const active = role === r;
                  return (
                    <button
                      key={r}
                      onClick={() => handleRoleChange(r)}
                      style={{
                        ...S.roleCard,
                        border: `2px solid ${active ? m.color : 'rgba(255,255,255,0.08)'}`,
                        background: active ? m.bg : 'rgba(255,255,255,0.03)',
                        boxShadow: active ? `0 0 14px ${m.color}40` : 'none',
                      }}
                    >
                      <span style={{ ...S.roleCardLabel, color: active ? m.color : 'rgba(255,255,255,0.6)' }}>
                        {m.label}
                      </span>
                      <span style={S.roleCardDesc}>{m.desc}</span>
                    </button>
                  );
                })}
              </div>

              {/* Current role info strip */}
              <div style={{ ...S.roleInfoStrip, background: roleMeta.bg, border: `1px solid ${roleMeta.color}30` }}>
                <span style={{ color: roleMeta.color, fontWeight: 600, fontSize: '0.8rem' }}>
                  {roleMeta.label}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>
                  {grantedCount} of {pages.length} pages accessible
                </span>
              </div>
            </div>

            {/* ── Quick actions ── */}
            <div className="modal-actions" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12, marginTop: 0 }}>
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginRight: 'auto' }}>
                Fine-tune page access:
              </span>
              <button onClick={grantAll} className="btn-secondary">✅ Grant All</button>
              <button onClick={revokeAll} className="btn-secondary">❌ Revoke All</button>
            </div>

            {/* ── Page checkboxes ── */}
            <div className="pages-list">
              {Object.keys(pagesByCategory).map((category) => (
                <div key={category} className="page-category">
                  <h3>{category}</h3>
                  <div className="page-items">
                    {pagesByCategory[category].map((page) => (
                      <div key={page.key} className="page-item">
                        <label>
                          <input
                            type="checkbox"
                            checked={!!access[page.key]}
                            onChange={() => togglePage(page.key)}
                          />
                          <span className="page-icon">{page.icon}</span>
                          <span className="page-label">{page.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Error message ── */}
            {saveMsg && (
              <div style={S.errMsg}>{saveMsg}</div>
            )}

            {/* ── Footer ── */}
            <div className="modal-footer">
              <button onClick={onClose} className="btn-secondary">Cancel</button>
              <button onClick={handleSave} className="btn-auth" disabled={saving}>
                {saving ? 'Saving…' : `Save — Set as ${roleMeta.label}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const S = {
  roleSection: {
    padding: '14px 20px 0',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  roleSectionLabel: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  roleCards: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
  },
  roleCard: {
    display: 'flex', flexDirection: 'column', gap: 4,
    padding: '10px 12px', borderRadius: 10,
    cursor: 'pointer', textAlign: 'left',
    transition: 'all 0.2s',
  },
  roleCardLabel: {
    fontSize: '0.85rem', fontWeight: 700,
  },
  roleCardDesc: {
    fontSize: '0.65rem', color: 'rgba(255,255,255,0.38)', lineHeight: 1.3,
  },
  roleInfoStrip: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '7px 14px', borderRadius: 8,
  },
  errMsg: {
    margin: '0 20px',
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#fca5a5',
    borderRadius: 8, padding: '8px 12px',
    fontSize: '0.78rem',
  },
};

export default UserAccessModal;
